import { useMemo } from "react";
import { useAppContext } from "src/hooks/useAppContext";
import { useChannelCompareChangesByIdQuery } from "src/types.g";
import { ChannelCompareChanges } from "../channelCompareTypes";
import { prepareChannelCompareChanges } from "../prepareChannelCompareChanges";

/**
 * Data seam for the Channel Compare Changes modal (GRWBKK-2041): the
 * channelCompareChangesById query (GRWBKK-2038) mapped for the selected zone
 * by prepareChannelCompareChanges (GRWBKK-2039).
 */

export interface UseChannelCompareChangesOptions {
  channelId: string;
  /**
   * Zone to compare; the main zone when undefined or no longer present (a
   * realtime update can remove the selected zone while the modal is open).
   */
  selectedZone?: string;
}

export interface UseChannelCompareChangesResult {
  compare?: ChannelCompareChanges;
  draftId?: string;
  loading: boolean;
  /** The query failed or the diff could not be prepared. */
  hasError: boolean;
  refetch: () => Promise<unknown>;
}

export const useChannelCompareChanges = ({
  channelId,
  selectedZone,
}: UseChannelCompareChangesOptions): UseChannelCompareChangesResult => {
  const context = useAppContext();
  const { data, error, loading, refetch } = useChannelCompareChangesByIdQuery({
    variables: { id: channelId },
    fetchPolicy: "cache-and-network",
    // Surface refetch (retry / realtime recalculation) as `loading` so the
    // retry-from-error transition can show the loading state.
    notifyOnNetworkStatusChange: true,
  });
  const channel = data?.channelById;

  // If the mapper throws on unexpected data, degrade to the modal's error
  // state (recoverable via Retry) rather than crashing the render.
  const { compare, prepareError } = useMemo(() => {
    if (!channel) {
      return { compare: undefined, prepareError: false };
    }
    try {
      return {
        compare: prepareChannelCompareChanges(channel, selectedZone, {
          intl: context.intl,
          secureMediaPolicy: context.secureMediaPolicy,
        }),
        prepareError: false,
      };
    } catch {
      return { compare: undefined, prepareError: true };
    }
  }, [channel, selectedZone, context.intl, context.secureMediaPolicy]);

  return {
    compare,
    draftId: channel?.draft?.id ?? undefined,
    loading,
    hasError: Boolean(error) || prepareError,
    refetch,
  };
};
